import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Form, Modal } from "react-bootstrap";
import Select from "react-select";
import {
  Card,
  CardInput,
  cardInput,
  getMakers,
  getToolOptions,
  getTools,
  saveCard,
  toolLabel,
} from "../../api/tooling";
import { ErrorNotice } from "./ToolingShared";

export default function CardForm({
  card,
  onClose,
  onSaved,
}: {
  card?: Card;
  onClose: () => void;
  onSaved: (card: Card) => void;
}) {
  const client = useQueryClient();
  const options = useQuery({
    queryKey: ["tooling", "options"],
    queryFn: getToolOptions,
  });
  const tools = useQuery({
    queryKey: ["tooling", "tools"],
    queryFn: () => getTools(),
  });
  const makers = useQuery({
    queryKey: ["tooling", "makers"],
    queryFn: getMakers,
  });
  const [data, setData] = useState<CardInput>(
    card
      ? cardInput(card)
      : {
          tool_id: 0,
          issue_type: "regrinding",
          points: 1,
          detail: "",
          status: "new_requirement",
          maker_ids: [],
        }
  );
  const mutation = useMutation({
    mutationFn: () => saveCard(data, card?.id),
    onSuccess: async (saved) => {
      await client.invalidateQueries({ queryKey: ["tooling"] });
      onClose();
      onSaved(saved);
    },
  });
  const toolOptions =
    tools.data?.map((t) => ({ value: t.id, label: toolLabel(t) })) || [];
  const makerOptions =
    makers.data?.map((m) => ({ value: m.id, label: m.name, color: m.color })) ||
    [];
  return (
    <Modal
      show
      onHide={() => !mutation.isLoading && onClose()}
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {card ? `Edit Development #${card.id}` : "New Tool Development"}
        </Modal.Title>
      </Modal.Header>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <Modal.Body>
          <ErrorNotice
            error={options.error || tools.error || makers.error}
            retry={() => {
              options.refetch();
              tools.refetch();
              makers.refetch();
            }}
          />
          <ErrorNotice error={mutation.error} />
          {card && (
            <p className="text-muted">
              Editing card information requires fresh approval. Stage changes
              keep the current approval.
            </p>
          )}
          <Form.Group className="mb-3" controlId="card-tool">
            <Form.Label>Tool · first linked part</Form.Label>
            <Select
              inputId="card-tool"
              options={toolOptions}
              value={toolOptions.find((t) => t.value === data.tool_id) || null}
              onChange={(o) => setData({ ...data, tool_id: o?.value || 0 })}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="card-makers">
            <Form.Label>Die makers</Form.Label>
            <Select
              inputId="card-makers"
              isMulti
              options={makerOptions}
              value={makerOptions.filter((m) =>
                data.maker_ids.includes(m.value)
              )}
              onChange={(items) =>
                setData({ ...data, maker_ids: items.map((i) => i.value) })
              }
              formatOptionLabel={(m) => (
                <span>
                  <span
                    className="tooling-dot me-2"
                    style={{ backgroundColor: m.color }}
                  />
                  {m.label}
                </span>
              )}
            />
            <Form.Text>
              Approved, completed points are split equally between selected die
              makers.
            </Form.Text>
          </Form.Group>
          <div className="row">
            <Form.Group className="col-md-8 mb-3" controlId="card-issue">
              <Form.Label>Type of issue</Form.Label>
              <Form.Select
                value={data.issue_type}
                onChange={(e) => {
                  const issue = options.data?.issues.find(
                    (o) => o.value === e.target.value
                  );
                  if (issue)
                    setData({
                      ...data,
                      issue_type: issue.value,
                      points: issue.points ?? 0,
                    });
                }}
              >
                {options.data?.issues.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-4 mb-3" controlId="card-points">
              <Form.Label>Points</Form.Label>
              <Form.Control
                type="number"
                required
                min={0}
                max={10000}
                step="0.01"
                value={Number.isNaN(data.points) ? "" : data.points}
                onChange={(e) =>
                  setData({
                    ...data,
                    points:
                      e.target.value === "" ? NaN : Number(e.target.value),
                  })
                }
              />
              <Form.Text>Suggested points can be edited.</Form.Text>
            </Form.Group>
          </div>
          <Form.Group className="mb-3" controlId="card-detail">
            <Form.Label>Detail</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              required
              maxLength={10000}
              value={data.detail}
              onChange={(e) => setData({ ...data, detail: e.target.value })}
            />
          </Form.Group>
          <Form.Group controlId="card-status">
            <Form.Label>Stage</Form.Label>
            <Form.Select
              value={data.status}
              onChange={(e) => setData({ ...data, status: e.target.value })}
            >
              {options.data?.statuses.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text>
              Use Pending for non-urgent tool work and ideas.
            </Form.Text>
          </Form.Group>
          {tools.data?.length === 0 && (
            <p className="text-muted mt-3">
              Add a tool in Inventory → Toolings before creating a card.
            </p>
          )}
          {makers.data?.length === 0 && (
            <p className="text-muted mt-3">
              Add a die maker using + Die Making before creating a card.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={mutation.isLoading}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              mutation.isLoading ||
              !options.data ||
              !tools.data ||
              !makers.data ||
              !data.tool_id ||
              !data.maker_ids.length ||
              !data.detail.trim()
            }
          >
            {mutation.isLoading ? "Saving…" : "Save card"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
